import * as duckdb from '@duckdb/duckdb-wasm';

// Using JSDelivr for zero-config CDN hosting of the WASM binaries
const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();

let _db: duckdb.AsyncDuckDB | null = null;
let _connection: duckdb.AsyncDuckDBConnection | null = null;

/**
 * Initializes the DuckDB engine singleton.
 */
export const getDuckDB = async (): Promise<duckdb.AsyncDuckDB> => {
    if (_db) return _db;

    // Select the best bundle for the current browser
    const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
    
    // Create a Web Worker for DuckDB
    const worker_url = URL.createObjectURL(
        new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'text/javascript' })
    );
    const worker = new Worker(worker_url);
    
    // Instantiate DuckDB
    const logger = new duckdb.ConsoleLogger();
    _db = new duckdb.AsyncDuckDB(logger, worker);
    await _db.instantiate(bundle.mainModule, bundle.pthreadWorker);
    URL.revokeObjectURL(worker_url);

    return _db;
};

/**
 * Pre-warms the DuckDB engine during idle time to eliminate cold-start latency.
 */
export const prewarmDuckDB = () => {
    if (typeof window === 'undefined') return;
    const schedule = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 2000));
    schedule(() => {
        getDuckDB().catch(() => {});
    });
};

/**
 * Gets a shared connection to the DuckDB instance.
 */
export const getConnection = async (): Promise<duckdb.AsyncDuckDBConnection> => {
    if (_connection) return _connection;
    const db = await getDuckDB();
    _connection = await db.connect();
    return _connection;
};

export const isDuckDBInitialized = (): boolean => {
    return _db !== null && _connection !== null;
};

/**
 * Ingests an array of JSON objects into a DuckDB table.
 */
export const ingestJSON = async (tableName: string, inputData: any) => {
    if (!isDuckDBInitialized()) {
        // Return silently if DuckDB has not been explicitly initialized
        return;
    }
    const db = await getDuckDB();
    const conn = await getConnection();

    const records = Array.isArray(inputData) ? inputData : (inputData?.data || inputData?.records || inputData?.rows || inputData?.snapshots || []);
    if (!Array.isArray(records) || records.length === 0) {
        console.warn(`[DuckDB] Skipping ingestion for ${tableName}: No valid records array found.`);
        return;
    }

    // Create a JSON string from the data and insert it using duckdb's JSON reader
    const jsonString = JSON.stringify(records);
    
    // Register the JSON string as a file in DuckDB's virtual filesystem
    const fileName = `${tableName}.json`;
    await db.registerFileText(fileName, jsonString);

    // Create a table from the JSON file (setting maximum_object_size to 100MB to prevent buffer overflow)
    await conn.query(`
        DROP TABLE IF EXISTS ${tableName};
        CREATE TABLE ${tableName} AS SELECT * FROM read_json_auto('${fileName}', maximum_object_size=104857600);
    `);
    
    console.log(`[DuckDB] Ingested ${records.length} records into table: ${tableName}`);
};

/**
 * Executes a SQL query and returns the results as a standard array of objects.
 */
export const query = async (sql: string): Promise<any[]> => {
    const conn = await getConnection();
    const result = await conn.query(sql);
    // Convert Apache Arrow Table to a standard JS Array of Objects
    return result.toArray().map((row: any) => row.toJSON());
};
